// Espelho em TypeScript do protocolo definido em server/internal/world/protocol.go.
// Se mudar um lado, mude o outro.

/**
 * Avatar montado no customizador. Mesmo formato do servidor (model.Appearance)
 * e do catalogo gerado em src/avatar/catalog.json.
 */
export interface Appearance {
  skinTone: number;
  hair: string;
  hairColor: number;
  top: string;
  topColor: number;
  bottom: string;
  bottomColor: number;
  shoes: string;
  shoesColor: number;
  /** Segunda cor das pecas de cima estampadas (xadrez, listrada...). */
  topColor2: number;
  /** Acessorios: "nenhum" quando nao ha peca (ver CATALOG.none). */
  facialHair: string;
  headwear: string;
  headwearColor: number;
  glasses: string;
  earrings: string;
}

export interface PublicUser {
  id: string;
  username: string;
  /** Apelido publico (chat, mundo, ranking) — cai para `username` se nunca foi escolhido. */
  displayName: string;
  skin: number;
  /** null = ainda nao montou o avatar: vale o preset `skin` e o customizador abre no login. */
  appearance: Appearance | null;
  /** Clicou no link de confirmacao. Opcional: nao bloqueia nada, so some o lembrete. */
  emailVerified: boolean;
  /** Cargo da conta: moderadores e admins veem as ferramentas de moderacao. */
  role: Role;
}

export type Role = "player" | "moderator" | "admin";

export function isStaff(role: Role | undefined): boolean {
  return role === "moderator" || role === "admin";
}

export interface MapInfo {
  key: string;
  url: string;
  width: number;
  height: number;
  tileSize: number;
  pixelWidth: number;
  pixelHeight: number;
  spawnX: number;
  spawnY: number;
}

export interface Interactable {
  id: number;
  name: string;
  /** "minigame" abre um jogo; "leaderboard" abre o painel de recordes; "seat" senta. */
  kind: string;
  game: string;
  label: string;
  x: number;
  y: number;
  radius: number;
  /** So nos assentos: onde o jogador fica de pe ao levantar. */
  standX?: number;
  standY?: number;
}

export const KIND_SEAT = "seat";

export interface PlayerInfo {
  id: string;
  name: string;
  skin: number;
  /** ausente = desenha o preset `skin` */
  appearance?: Appearance;
  x: number;
  y: number;
  /** Visitante sem conta. O nome vem vazio para quem nao e da moderacao. */
  ghost?: boolean;
}

/** Direcoes, na mesma ordem das linhas do spritesheet do avatar. */
export const DIRECTIONS = ["down", "left", "right", "up"] as const;
export type Direction = (typeof DIRECTIONS)[number];

export interface PlayerState {
  id: string;
  x: number;
  y: number;
  /** indice da direcao */
  d: number;
  /** esta se movendo */
  m: boolean;
  /** id do assento ocupado; ausente = de pe */
  s?: number;
  /** gesto em loop (id de CATALOG.gestures); ausente = nenhum */
  g?: string;
  /** aura ativa (easter egg); ausente = nenhuma */
  a?: string;
  /** veio de outra instancia do servidor */
  r?: boolean;
}

export interface WelcomeMessage {
  t: "welcome";
  /** No fantasma: ghost = true e nome vazio (o id serve para se achar no snapshot). */
  you: PlayerInfo | null;
  ghost: boolean;
  tickRate: number;
  snapshotRate: number;
  walkSpeed: number;
  map: MapInfo;
  interactables: Interactable[];
  online: number;
  serverTime: number;
}

export interface StateMessage {
  t: "state";
  tick: number;
  joined?: PlayerInfo[];
  left?: string[];
  players: PlayerState[];
  ack?: number;
  /** progresso (0 a 1) da carga de aura do proprio jogador; ausente = sem barrinha */
  charge?: number;
  online: number;
}

export interface ChatMessage {
  t: "chat";
  id: string;
  name: string;
  text: string;
  at: number;
}

export interface InteractMessage {
  t: "interact";
  kind: string;
  game?: string;
  label?: string;
  name?: string;
}

export interface SystemMessage {
  t: "system";
  message: string;
  level?: "info" | "warn" | "error";
  /** Avisos que exigem tratamento especial, ex.: SESSION_REPLACED. */
  code?: string;
}

/** A conta abriu em outra aba: esta conexao caiu e nao deve reconectar. */
export const SESSION_REPLACED = "session_replaced";

/**
 * Codigos da moderacao que derrubam a conexao (server/internal/world/protocol.go).
 * O cliente nao reconecta sozinho: volta para a tela de entrada ou, no
 * profile_reset, recarrega o perfil e entra de novo.
 */
export const KICK_CODES = ["kicked", "suspended", "banned", "session_ended", "profile_reset"] as const;
export type KickCode = (typeof KICK_CODES)[number];
export const PROFILE_RESET = "profile_reset";
/** Avisos que tambem aparecem em destaque no topo da tela. */
export const BANNER_CODES = ["announce", "mod_warning", "muted"];

export interface PongMessage {
  t: "pong";
  ts: number;
}

export type ServerMessage =
  | WelcomeMessage
  | StateMessage
  | ChatMessage
  | InteractMessage
  | SystemMessage
  | PongMessage;

// --------------------------------------------------------------- REST

export interface Game {
  slug: string;
  name: string;
  maxScore: number;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  skin: number;
  score: number;
  createdAt: string;
}

export interface LeaderboardResponse {
  game: Game;
  entries: LeaderboardEntry[];
  personalBest?: number;
}

export interface ScoreResult {
  score: number;
  personalBest: number;
  isNewBest: boolean;
  rank: number;
}

// --------------------------------------------------------- moderacao

export type SanctionKind = "mute" | "suspend" | "ban";

export interface Sanction {
  id: number;
  userId: string;
  kind: SanctionKind;
  reason: string;
  issuedBy: string;
  createdAt: string;
  /** null = permanente */
  expiresAt: string | null;
  revokedAt: string | null;
  revokedBy?: string;
}

export interface ActiveSanctions {
  mute: Sanction | null;
  suspend: Sanction | null;
  ban: Sanction | null;
}

export interface OnlinePlayer {
  id: string;
  name: string;
  username: string;
  role: Role;
  mute: Sanction | null;
  /** O cargo de quem pede e maior que o deste jogador. */
  canModerate: boolean;
}

/** Fantasma na lista da moderacao: apelido gerado e IP (por ele e banido). */
export interface OnlineGhost {
  id: string;
  name: string;
  ip: string;
}

/** Banimento de IP para a entrada como fantasma. */
export interface GhostBan {
  id: number;
  ip: string;
  ghostName: string;
  reason: string;
  issuedBy: string;
  createdAt: string;
}

export interface AdminUser {
  id: string;
  username: string;
  /** "" = usa o nome de usuario */
  displayName: string;
  email: string;
  emailVerified: boolean;
  role: Role;
  createdAt: string;
  online: boolean;
  sanctions: ActiveSanctions;
}

export interface ModAction {
  id: number;
  actorId: string | null;
  actorName: string;
  targetId: string | null;
  targetName: string;
  action: string;
  detail: string;
  createdAt: string;
}

export interface Page {
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminUserDetail {
  user: AdminUser;
  canModerate: boolean;
  history: Sanction[];
  actions: ModAction[];
  scores: { game: string; best: number; plays: number }[];
}

// --------------------------------------------------------- denuncias

export type ReportCategory = "offense" | "spam" | "name" | "cheat" | "other";
export type ReportStatus = "open" | "resolved" | "dismissed";

export const REPORT_CATEGORIES: Record<ReportCategory, string> = {
  offense: "Ofensa ou assédio",
  spam: "Spam ou flood",
  name: "Nome ou avatar impróprio",
  cheat: "Trapaca",
  other: "Outro motivo",
};

/** Codigo de sistema que avisa moderadores sobre uma denuncia nova. */
export const REPORT_NEW = "report_new";

export interface Report {
  id: number;
  reporterId: string | null;
  reporterName: string;
  targetId: string | null;
  targetName: string;
  /** null = a conta denunciada foi excluida */
  targetRole: Role | null;
  category: ReportCategory;
  details: string;
  /** Ultimas falas do denunciado, capturadas pelo servidor. */
  evidence: { text: string; at: number }[];
  status: ReportStatus;
  handledBy: string | null;
  handledAt: string | null;
  note: string;
  createdAt: string;
}

export interface BlockedTerm {
  term: string;
  createdBy: string;
  createdAt: string;
}
